import { type Logger, getEpochColumn } from 'ag-charts-core';

import type {
    ColumnValueType,
    ColumnValueTypeMapping,
    GroupedData,
    ProcessedData,
    ProcessedDataDef,
    PropertyDefinition,
    ScopeProvider,
    SortOrderEntry,
    UngroupedData,
} from '../../dataModelTypes';
import { COLUMN_SORT_ORDERS, DOMAIN_RANGES, KEY_SORT_ORDERS } from '../../dataModelTypes';
import { RangeLookup } from '../../rangeLookup';
import { type SortOrder, valuesSortOrder } from '../../sortOrder';
import type { DataModelContext } from '../dataModelContext';

const NUMERIC_COLUMN_TYPES = new Set<ColumnValueType>(['number', 'bigint', 'mixed-numeric']);

// 'object' is caller-typed (the caller asserts the element type), so any runtime tag is accepted.
function isCompatibleColumnType(expected: ColumnValueType, actual: ColumnValueType): boolean {
    switch (expected) {
        case 'number':
        case 'bigint':
        case 'mixed-numeric':
            return NUMERIC_COLUMN_TYPES.has(actual) || actual === 'date';
        case 'date':
            return actual === 'date' || NUMERIC_COLUMN_TYPES.has(actual);
        case 'string':
            // String-asserting consumers toString-coerce their values, so a stringifiable
            // object column (e.g. integrated category wrappers) is a compatible actual type.
            return actual === 'string' || actual === 'object';
        case 'boolean':
            return actual === 'boolean';
        case 'object':
            return true;
    }
}

// A null actualType (e.g. all-empty column) is a no-op, not a mismatch.
function assertColumnValueType(
    scope: ScopeProvider,
    searchId: string,
    expectedType: ColumnValueType,
    actualType: ColumnValueType | undefined,
    logger: Logger
): void {
    if (actualType == null || isCompatibleColumnType(expectedType, actualType)) return;
    logger.warnOnce(
        `column '${searchId}' for scope '${scope.id}' was resolved as '${expectedType}' but holds '${actualType}' values; check the series data types.`
    );
}

/**
 * DataModelResolvers handles lookups and resolution of processed data.
 *
 * RESOLVER RESPONSIBILITIES:
 * - Resolves property definitions by ID within a scope
 * - Looks up keys, columns, and values from processed data
 * - Manages domain and sort order resolution
 * - Provides range lookups for data access
 *
 * SCOPE-AWARE RESOLUTION:
 * - All lookups are scoped to specific data sources
 * - Uses internal scope cache for fast property definition lookup
 * - Throws clear errors when definitions are not found
 */
export class DataModelResolvers<D extends object, K extends keyof D & string> {
    private readonly rangeBetweenBuffer: [number, number] = [0, 0];

    constructor(private readonly ctx: DataModelContext<D, K>) {}

    resolveMissingDataCount(scope: ScopeProvider): number {
        let missingDataCount = 0;
        for (const value of this.ctx.values) {
            missingDataCount = Math.max(missingDataCount, value.missing.get(scope.id) ?? 0);
        }
        return missingDataCount;
    }

    resolveProcessedDataDefById(scope: ScopeProvider, searchId: string): ProcessedDataDef | never {
        const def = this.ctx.scopeCache.get(scope.id)?.get(searchId);

        if (!def) {
            throw new Error(`AG Charts - didn't find property definition for [${searchId}, ${scope.id}]`);
        }

        return { index: def.index, def };
    }

    resolveProcessedDataIndexById(scope: ScopeProvider, searchId: string): number {
        // Inlined to skip the `{ index, def }` allocation in resolveProcessedDataDefById on the hot path.
        const def = this.ctx.scopeCache.get(scope.id)?.get(searchId);
        if (!def) {
            throw new Error(`AG Charts - didn't find property definition for [${searchId}, ${scope.id}]`);
        }
        return def.index;
    }

    resolveKeysById<T = string>(
        scope: ScopeProvider,
        searchId: string,
        processedData: UngroupedData<any> | GroupedData<any>
    ): T[] {
        const index = this.resolveProcessedDataIndexById(scope, searchId);
        const keys = processedData.keys[index];
        if (keys == null) {
            throw new Error(`AG Charts - didn't find keys for [${searchId}, ${scope.id}]`);
        }
        return keys.get(scope.id) as T[];
    }

    hasColumnById(scope: ScopeProvider, searchId: string) {
        return this.ctx.scopeCache.get(scope.id)?.get(searchId) != null;
    }

    resolveColumnById<T extends Exclude<ColumnValueType, 'object'>>(
        scope: ScopeProvider,
        searchId: string,
        processedData: UngroupedData<any> | GroupedData<any>,
        expectedType: T
    ): ColumnValueTypeMapping[T][];
    resolveColumnById<E = unknown>(
        scope: ScopeProvider,
        searchId: string,
        processedData: UngroupedData<any> | GroupedData<any>,
        expectedType: 'object'
    ): E[];
    resolveColumnById<E>(
        scope: ScopeProvider,
        searchId: string,
        processedData: UngroupedData<any> | GroupedData<any>,
        expectedType: ColumnValueType
    ): E[] {
        const index = this.resolveProcessedDataIndexById(scope, searchId);
        const column = processedData.columns?.[index];
        if (column == null) {
            throw new Error(`AG Charts - didn't find column for [${searchId}, ${scope.id}]`);
        }
        assertColumnValueType(scope, searchId, expectedType, processedData.columnValueType?.[index], this.ctx.logger);
        return column as E[];
    }

    resolveColumnNeedsValueOf(
        scope: ScopeProvider,
        searchId: string,
        processedData: UngroupedData<any> | GroupedData<any>
    ): boolean {
        const index = this.resolveProcessedDataIndexById(scope, searchId);
        return processedData.columnNeedValueOf?.[index] ?? true;
    }

    /**
     * Converts a relative datum index to an absolute column index.
     *
     * INDEXING STRATEGY:
     * - Relative index: Offset from the start of a group (stored in datumIndices)
     * - Absolute index: Position in the full column array
     * - Conversion: absoluteIndex = groupIndex + relativeIndex
     *
     * When groupsUnique=true, relativeIndex is always 0, making this a simple
     * identity mapping. This optimization reduces memory usage significantly
     * for large datasets with unique keys.
     *
     * @param groupIndex index of the group in ProcessedData.groups
     * @param relativeDatumIndex relative index stored in group.datumIndices
     * @returns absolute index for accessing columns
     */
    resolveAbsoluteIndex(groupIndex: number, relativeDatumIndex: number): number {
        return groupIndex + relativeDatumIndex;
    }

    getDomain(
        scope: ScopeProvider,
        searchId: string,
        type: PropertyDefinition<any>['type'],
        processedData: ProcessedData<K>
    ): any[] | [number, number] | [] {
        const domains = this.getDomainsByType(type ?? 'value', processedData);
        return domains?.[this.resolveProcessedDataIndexById(scope, searchId)] ?? [];
    }

    getDomainBetweenRange(
        scope: ScopeProvider,
        searchIds: string[],
        [i0, i1]: [number, number],
        processedData: ProcessedData<K>
    ): [number, number] {
        const columnIndices = searchIds.map((searchId) => this.resolveProcessedDataIndexById(scope, searchId));

        // Fast path: when range covers all data, use pre-computed domains directly
        // This avoids building the RangeLookup segment tree for unzoomed views
        const dataLength = processedData.input.count;
        if (i0 <= 0 && i1 >= dataLength) {
            const domains = processedData.domain.values;
            let min = Infinity;
            let max = -Infinity;
            for (const columnIndex of columnIndices) {
                const domain = domains[columnIndex] as [number, number] | undefined;
                if (domain != null) {
                    if (domain[0] < min) min = domain[0];
                    if (domain[1] > max) max = domain[1];
                }
            }
            this.rangeBetweenBuffer[0] = min;
            this.rangeBetweenBuffer[1] = max;
            return this.rangeBetweenBuffer;
        }

        const cacheKey = columnIndices.join(':');
        const domainRanges = processedData[DOMAIN_RANGES];
        const values = columnIndices.map((columnIndex) => processedData.columns[columnIndex]);
        let rangeLookup = domainRanges.get(cacheKey);
        if (rangeLookup == null) {
            rangeLookup = new RangeLookup(values);
            domainRanges.set(cacheKey, rangeLookup);
        } else if (rangeLookup.isDirty) {
            rangeLookup.rebuild(values);
            rangeLookup.isDirty = false;
        }
        return rangeLookup.rangeBetween(i0, i1, this.rangeBetweenBuffer);
    }

    private getSortOrder(
        values: any[],
        index: number,
        sortOrders: Map<number, SortOrderEntry>,
        needsValueOf: boolean
    ): SortOrder {
        const entry = sortOrders.get(index);
        // Recalculate if missing or marked dirty by incremental cache management
        if (entry == null || entry.isDirty) {
            // Reuse the parse-once epoch column for ISO-string time columns rather than re-parsing.
            const epochs = getEpochColumn(values);
            const newEntry: SortOrderEntry = {
                sortOrder:
                    epochs != null && epochs !== values
                        ? valuesSortOrder(epochs, false)
                        : valuesSortOrder(values, needsValueOf),
            };
            sortOrders.set(index, newEntry);
            return newEntry.sortOrder;
        }
        return entry.sortOrder;
    }

    getKeySortOrder(scope: ScopeProvider, searchId: string, processedData: ProcessedData<K>): SortOrder {
        const columnIndex = this.resolveProcessedDataIndexById(scope, searchId);
        const keys = processedData.keys[columnIndex]?.get(scope.id);
        // Key columns typically contain dates/objects, so default to true for needsValueOf
        return keys ? this.getSortOrder(keys, columnIndex, processedData[KEY_SORT_ORDERS], true) : undefined;
    }

    getKeySortEntry(
        scope: ScopeProvider,
        searchId: string,
        processedData: ProcessedData<K>
    ): SortOrderEntry | undefined {
        const columnIndex = this.resolveProcessedDataIndexById(scope, searchId);
        const entry = processedData[KEY_SORT_ORDERS].get(columnIndex);
        // Return undefined if entry is dirty (needs recalculation)
        return entry?.isDirty ? undefined : entry;
    }

    getColumnSortOrder(scope: ScopeProvider, searchId: string, processedData: ProcessedData<K>): SortOrder {
        const columnIndex = this.resolveProcessedDataIndexById(scope, searchId);
        // Use columnNeedValueOf metadata to determine if valueOf() is needed
        const needsValueOf = processedData.columnNeedValueOf?.[columnIndex] ?? true;
        return this.getSortOrder(
            processedData.columns[columnIndex],
            columnIndex,
            processedData[COLUMN_SORT_ORDERS],
            needsValueOf
        );
    }

    private getDomainsByType(type: PropertyDefinition<any>['type'], processedData: ProcessedData<K>) {
        switch (type) {
            case 'key':
                return processedData.domain.keys;
            case 'value':
                return processedData.domain.values;
            case 'aggregate':
                return processedData.domain.aggValues;
            case 'group-value-processor':
                return processedData.domain.groups;
            default:
                return null;
        }
    }
}
